/**
 * Application Entry Point
 *
 * Performs a silent single sign-on check before rendering: an active session is
 * hydrated (token + account record) so the app boots already authenticated;
 * otherwise it renders anonymous and React Admin drives the user to sign in.
 */

import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import './styles/index.css';
import { initSso, getAccessToken, onToken } from './auth/sso';
import { hydrateSession, syncToken } from './auth/authSync';
import { clearAuthData } from './utils/auth';
import { isPublicOnboardingRoute } from './onboarding/publicRoutes';
import PublicApp from './onboarding/PublicApp';

async function bootstrap(): Promise<void> {
  // Public onboarding routes are the only pre-identity surfaces: render the
  // self-contained funnel and skip the authenticated boot entirely (no silent
  // sign-on check, no portal redirect).
  if (isPublicOnboardingRoute()) {
    ReactDOM.createRoot(document.getElementById('root')!).render(
      <React.StrictMode>
        <PublicApp />
      </React.StrictMode>
    );
    return;
  }

  // Keep the stored token in sync with silent token refreshes.
  onToken((token) => syncToken(token));

  try {
    const authenticated = await initSso();
    if (authenticated) {
      const token = getAccessToken();
      if (token) {
        await hydrateSession(token);
      }
    } else {
      clearAuthData();
    }
  } catch {
    // A failed session check must not block the app: render anonymous and let
    // the auth flow redirect to sign-in.
    clearAuthData();
  }

  ReactDOM.createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
      <App />
    </React.StrictMode>
  );
}

bootstrap();
